/**
 * A sentence's worth of detail, folded away until somebody asks for it.
 *
 * ## Why this exists at all, and why it is not `AdvancedDisclosure`
 *
 * Slice 16 established the disclosure pattern on `/integrations` and this file
 * was written **after** reading `routes/integrations/AdvancedDisclosure.tsx`, not
 * instead of it. That component folds away a *section of expert controls* — the
 * SNS topic, the redirect subdomain, VDM — and its whole shape says so: a
 * bordered card, a mono `ADVANCED` eyebrow, a 24px stack inside. Wrapping a
 * single explanatory sentence in it would draw a heavier box around the
 * explanation than around the field it explains.
 *
 * What the template screens need is the other half of the same rule. Slice 17's
 * text rule sorts prose into three kinds, and the third — *instruction* — is
 * meant to be "short by default, detail on demand, still reachable without
 * leaving the screen". A field description is one line; the paragraph that used
 * to follow it goes in here. Same mechanism, a tenth of the furniture.
 *
 * **It deliberately copies `AdvancedDisclosure`'s mechanics rather than its
 * looks.** `<details>`/`<summary>`, marker suppressed, chevron rotated by
 * `group-open`, the shared `focusRing`. That is the part slice 16 argued for at
 * length and it is the part that must not fork: the state, the keyboard
 * behaviour and the disclosure role come from the element, so there is no
 * `aria-expanded` here to stop being updated, and the children are **in the
 * DOM while it is closed**, so Ctrl+F finds a postal address or a limit that has
 * been folded away. A conditionally rendered detail is a detail nobody can
 * search for, which is how a disclosure becomes the place things go to be lost.
 *
 * ## The rejected alternative, and the one that came before it
 *
 * `title=` on a `<span>` — a tooltip — was rejected outright, and the slice
 * brief says why in one line: a skimmer never opens a tooltip, tooltips do not
 * exist on touch, and they are not reachable by keyboard. Anything a person
 * could draw a false conclusion without is not allowed in here at all; it stays
 * on the screen in four words. This component is only ever for the *third* kind
 * of text.
 *
 * Copying `AdvancedDisclosure` into this directory and shrinking it was the
 * other option, and it is worse than a second component with a different job:
 * two files with the same name and different padding is exactly the accretion
 * slice 16 spent itself undoing.
 *
 * **Both of these should end up in `components/`.** `AdvancedDisclosure` is on
 * an `/integrations` route and is imported by nothing outside it; this one is on
 * a `/templates` route with the same problem in mirror image. Neither is
 * route-specific and a third screen wanting either will reach across a route
 * boundary to get it. That move is not made here because slice 17 runs four
 * agents over four route folders in parallel and a shared file moved under three
 * of them mid-slice is a merge nobody asked for. It is written down in the slice
 * report instead.
 */
import { cn, focusRing } from '@wend/ui';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

export interface MoreDetailProps {
  /**
   * What is behind it, named — not teased.
   *
   * "More" is the failure mode: a person scanning for the postal-address rules
   * cannot tell from it whether this is where they went, so they open every one
   * on the page and then stop opening any. `AdvancedDisclosure`'s header makes
   * the same argument about its own summary and it applies with more force here,
   * because these are small and there are several on a screen.
   */
  readonly summary: string;
  readonly children: ReactNode;
  readonly className?: string;
}

export function MoreDetail({ summary, children, className }: MoreDetailProps) {
  return (
    <details className={cn('group', className)}>
      <summary
        className={cn(
          'inline-flex cursor-pointer list-none items-center gap-1.5 rounded-sm py-0.5',
          'text-[0.8rem] text-muted hover:text-text',
          '[&::-webkit-details-marker]:hidden',
          focusRing,
        )}
      >
        <ChevronRight
          aria-hidden="true"
          className="size-3.5 shrink-0 text-muted transition-transform duration-150 ease-wend group-open:rotate-90"
        />
        {summary}
      </summary>

      <div className="mt-2 flex max-w-prose flex-col gap-2 border-l border-line pl-3 text-[0.82rem] leading-relaxed text-muted">
        {children}
      </div>
    </details>
  );
}
